import { useState, useCallback, useMemo } from "react";
import { Server, Wifi } from "lucide-react";
import styles from "./ApiSettings.module.css";
import AddApiSettingModal from "../../../components/System&Settings/ApiSettings/AddApiSettingModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  useGetApiSettings,
  useCreateApiSetting,
  useUpdateApiSetting,
  useDeleteApiSetting,
  useToggleApiSettingStatus,
  useTestApiConnection,
} from "../../../api/sytem&settings/useApiSettings";


const ApiSettings = () => {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: rows, pagination, loading, error, refetch } = useGetApiSettings({
    page,
    limit,
    search,
    type: typeFilter,
    active: statusFilter,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createApiSetting, loading: createLoading } = useCreateApiSetting();
  const { execute: updateApiSetting, loading: updateLoading } = useUpdateApiSetting();
  const { execute: deleteApiSetting, loading: deleteLoading } = useDeleteApiSetting();
  const { execute: toggleStatus } = useToggleApiSettingStatus();
  const { execute: testConnection } = useTestApiConnection();

  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const getDisplayValue = (row, field) =>
    pendingChanges[row.id]?.[field] ?? row[field];

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
    setPendingChanges({});
  };

  const handleAdd = () => {
    if (loading || toggleSaveLoading) return;
    setMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    if (loading || toggleSaveLoading) return;
    setMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteApiSetting(rowToDelete.id);
      showToast("API setting deleted", "success");
      refetch();
      setRowToDelete(null);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete", "error");
    }
  };

  const handleTest = async (row) => {
    setTestingId(row.id);
    try {
      const res = await testConnection(row.id);
      if (res?.success) {
        showToast(`Connection to ${row.name} successful`, "success");
      } else {
        showToast(res?.message || "Connection test failed", "error");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Connection test failed", "error");
    } finally {
      setTestingId(null);
    }
  };

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return { ...prev, [row.id]: { ...current, [field]: nextVal } };
    });
  }, []);

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      for (const id of ids) {
        const patch = pendingChanges[id];
        if (patch.active !== undefined) {
          await toggleStatus(id);
        }
      }
      setPendingChanges({});
      refetch();
      showToast("Changes saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        await createApiSetting(payload);
        showToast("API setting created successfully", "success");
      } else if (selectedRow?.id) {
        await updateApiSetting(selectedRow.id, payload);
        showToast("API setting updated successfully", "success");
      }
      setShowModal(false);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save API setting", "error");
    }
  };

  const getTypeBadge = (type) => {
    const map = {
      utility: { label: "Utility", cls: styles.badgeUtility },
      integration: { label: "Integration", cls: styles.badgeIntegration },
      export: { label: "Export", cls: styles.badgeExport },
    };
    const badge = map[type] || { label: type || "—", cls: styles.badgeDefault };
    return <span className={`${styles.badge} ${badge.cls}`}>{badge.label}</span>;
  };

  const getAuthBadge = (authType) => {
    const map = {
      api_key: "API Key",
      basic: "Basic Auth",
      oauth2: "OAuth2",
      bearer: "Bearer Token",
      none: "None",
    };
    return map[authType] || authType || "—";
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>
          <Server size={20} />
          API Settings
        </div>
        <button className={styles.add} onClick={handleAdd} disabled={loading || toggleSaveLoading}>
          + Add API
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ</span>
        <span>
          Manage all system, utility, and integration APIs. This is the single authoritative configuration for external services.
        </span>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by API name or base URL..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
            disabled={loading || toggleSaveLoading}
          />
          <button type="submit" className={styles.addbtn} disabled={loading || toggleSaveLoading}>
            Search
          </button>
        </form>
        <SelectBox
          placeholder="Type: All"
          value={typeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "Utility", value: "utility" },
            { label: "Integration", value: "integration" },
            { label: "Export", value: "export" },
          ]}
          disabled={loading || toggleSaveLoading}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "true" },
            { label: "Inactive", value: "false" },
          ]}
          disabled={loading || toggleSaveLoading}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading API settings…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {rows.length} of {pagination.total} API configurations
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>API NAME</th>
                    <th>TYPE</th>
                    <th>BASE URL</th>
                    <th>AUTH TYPE</th>
                    <th>ENVIRONMENT</th>
                    <th>STATUS</th>
                    <th>LAST TESTED</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className={styles.noResults}>
                        No API settings found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td className={styles.apiName}>
                          <Server size={14} className={styles.apiIcon} />
                          {row.name ?? "—"}
                        </td>
                        <td>{getTypeBadge(row.type)}</td>
                        <td className={styles.urlCell}>
                          <span className={styles.url}>{row.baseUrl ?? "—"}</span>
                        </td>
                        <td className={styles.authType}>{getAuthBadge(row.authType)}</td>
                        <td>
                          <span className={`${styles.badge} ${row.environment === "production" ? styles.badgeProd : styles.badgeTest}`}>
                            {row.environment === "production" ? "Production" : "Test"}
                          </span>
                        </td>
                        <td>
                          <ToggleSwitch
                            checked={!!getDisplayValue(row, "active")}
                            onChange={() => applyToggle(row, "active")}
                            disabled={loading || toggleSaveLoading || deleteLoading}
                          />
                        </td>
                        <td className={styles.lastTested}>
                          {row.lastTestedAt
                            ? new Date(row.lastTestedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
                            : "Never"}
                        </td>
                        <td className={styles.rowActions}>
                          <button
                            onClick={() => handleTest(row)}
                            disabled={testingId === row.id || loading || toggleSaveLoading || deleteLoading}
                            className={styles.testBtn}
                          >
                            {testingId === row.id ? (
                              <span className={styles.spinnerSmall} aria-hidden />
                            ) : (
                              <Wifi size={13} />
                            )}
                            {testingId === row.id ? "Testing…" : "Test"}
                          </button>
                          <button
                            onClick={() => handleEdit(row)}
                            disabled={loading || toggleSaveLoading || deleteLoading}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            disabled={loading || toggleSaveLoading || deleteLoading}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                          disabled={loading || toggleSaveLoading}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || toggleSaveLoading || loading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddApiSettingModal
        open={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete API setting"
        itemName={rowToDelete?.name}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default ApiSettings;